import { Input } from '@sas/nova-commons/components/Input';
import { useDesignTokens } from '@sas/nova-commons/theme';
import React, { useLayoutEffect, useMemo, useState, useRef, useEffect } from 'react';
import { TextFieldState } from '../../store/birdElement/textFieldState';
import { setConstraints } from '../../store/birdLayout/BirdLayoutState';
import { useI18n } from '../../locale/I18nContext';
import { selectAccessibilityLabelState, selectBirdTextFieldState, selectStyleState, } from '../../store/selectors';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { filterUndefined, fontStyles, longestTextWidth, textHeight } from '../utils/style';
import { useFontLoadedKey } from '../FontProvider';
const inputWidthPadding = 16 /* left and right padding */ + 2; /* border */
const inputHeightPadding = 8 /* top and bottom padding */ + 2 /* border */ + 1; /* fudge factor */
export const BirdTextFieldBase = function (props) {
    const actions = props.actions;
    const { values = [], measureValues = [], committedValue = '', formattedValue = '', label, error, } = props.state;
    const localize = useI18n();
    const { font, normal: normalStyles } = props.style || {};
    const placeholderText = label
        ? localize({ key: 'TextFieldTypeAhead.placeholder.fmt', args: { placeholderLabel: label } })
        : null;
    const [liveValue, setLiveValue] = useState(committedValue);
    useLayoutEffect(() => {
        setLiveValue(committedValue);
    }, [committedValue]);
    const combinedValues = useMemo(() => values.map((value, index) => ({
        text: value,
        secondaryText: measureValues[index],
        concatSecondaryText: true,
    })), [values, measureValues]);
    {
        const preferredValues = props.preferredSizing ? combinedValues : undefined;
        const dt = useDesignTokens();
        const fonts = useMemo(() => fontStyles(font, dt), [font, dt]);
        const fontKey = useFontLoadedKey(fonts.fontFamily);
        useLayoutEffect(() => {
            const inputHeight = Math.max(28 /* min height 1.75rem */, textHeight(fonts) + inputHeightPadding);
            let preferredWidth = undefined;
            if (preferredValues) {
                const mappedValues = preferredValues.map((value) => value.text + (value.secondaryText || ''));
                if (placeholderText) {
                    mappedValues.push(placeholderText);
                }
                preferredWidth = longestTextWidth(mappedValues, fonts) + inputWidthPadding;
                // VAN has a min size of 160px
                preferredWidth = Math.max(160, preferredWidth);
            }
            actions.setConstraints(props.id, {
                minHeight: inputHeight,
                preferredHeight: inputHeight,
                minWidth: 160,
                preferredWidth,
            });
        }, [fontKey /*(implicit)*/, actions, preferredValues, props.id, placeholderText, fonts]);
    }
    const [justCommitted, setJustCommitted] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const showFormattedValue = error === null && formattedValue !== undefined && (!hasFocus || justCommitted);
    // Show the raw value if the user clicks or types any key after pressing Enter
    const refInput = useRef(null);
    useEffect(() => {
        if (!refInput.current || !justCommitted) {
            return;
        }
        const input = refInput.current;
        const onKeydown = (e) => {
            if (e.key === 'Enter') {
                return;
            }
            setJustCommitted(false);
        };
        const onClick = () => {
            setJustCommitted(false);
        };
        input.addEventListener('keydown', onKeydown);
        input.addEventListener('mousedown', onClick);
        return () => {
            input.removeEventListener('keydown', onKeydown);
            input.removeEventListener('mousedown', onClick);
        };
    }, [justCommitted]);
    return (React.createElement(Input, { style: {
            width: '100%',
        }, ref: refInput, futureTheme: () => {
            return filterUndefined({
                '--input-bg': normalStyles === null || normalStyles === void 0 ? void 0 : normalStyles.bg,
                '--input-fg': normalStyles === null || normalStyles === void 0 ? void 0 : normalStyles.fg,
                '--input-placeholder-fg': normalStyles === null || normalStyles === void 0 ? void 0 : normalStyles.fg,
                '--input-font-family': font === null || font === void 0 ? void 0 : font.fontFamily,
                '--input-fontSize': font === null || font === void 0 ? void 0 : font.fontSize,
                '--input-fontStyle': font === null || font === void 0 ? void 0 : font.fontStyle,
                '--input-fontWeight': font === null || font === void 0 ? void 0 : font.fontWeight,
                '--input-textDecoration': font === null || font === void 0 ? void 0 : font.textDecoration,
            });
        }, placeholder: placeholderText, suggestionItems: combinedValues, onChange: ({ value }) => {
            setJustCommitted(false);
            setLiveValue(value);
            actions.updateError(props.id, value);
        }, onCommit: (e) => {
            if (justCommitted) {
                // Committed without a change in value.
                // Returning here prevents the formatted value from being committed if the user has not modified it.
                return;
            }
            if (e.reason === 'enter') {
                setJustCommitted(true);
            }
            actions.commitValue(props.id, e.value);
        }, onCancel: () => {
            setLiveValue(committedValue);
            actions.updateError(props.id, committedValue);
            return liveValue !== committedValue;
        }, value: showFormattedValue ? formattedValue : liveValue, message: error ? localize(error) : null, status: error ? 'alert' : null, onFocus: () => {
            setHasFocus(true);
        }, onBlur: () => {
            setJustCommitted(false);
            setHasFocus(false);
        }, filterType: "contains", ariaLabel: props.accessibilityLabel, enableVirtualScrolling: true }));
};
export const BirdTextField = connect((state, props) => ({
    state: selectBirdTextFieldState(state, props.id),
    style: selectStyleState(state, props.id),
    accessibilityLabel: selectAccessibilityLabelState(state, props.id),
}), (dispatch) => ({
    actions: bindActionCreators(Object.assign({ setConstraints }, TextFieldState.actions), dispatch),
}))(BirdTextFieldBase);
